import { refreshTokenApi } from "@/api/services/main/auth";
import { CENTER_REFRESH_TOKEN_KEY, CENTER_TOKEN_KEY } from "@/constants/common";
import { ERROR_STATUS_CODES } from "@/constants/error";
import { AppRoutes } from "@/routes/config";
import { getCustomSentryExceptionError, handleApiErrorWithCode, isApiError } from "@/utils/error";
import { redirectToLogin } from "@/utils/redirectToLogin";
import { getMatchRoutes } from "@/utils/url";
import * as Sentry from "@sentry/react";
import axios, { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios";
import Cookies from "js-cookie";

if (!process.env.REACT_APP_API_HOST) {
  throw new Error("please provide REACT_APP_API_HOST in env");
}

export const baseURL = `${process.env.REACT_APP_API_HOST}`;

export const globalBaseURL = `${process.env.REACT_APP_API_HOST}/global`;

export const globalApi = axios.create({
  baseURL: globalBaseURL,
});

export const clientApi = axios.create({
  baseURL,
});

// If successful (do nothing)
export const onSuccess = (response: AxiosResponse) => {
  Sentry.endSession();
  return response;
};

type RetryableConfig = AxiosRequestConfig & { _isRetry?: boolean };

let isRefreshing = false;
// Each waiting request registers a callback that retries *its own* original
// config once the token is refreshed (or rejects if refresh failed).
let refreshQueue: ((token: string | null) => void)[] = [];

// Retry through the *same* axios instance that made the original request so
// the retried call goes back through its request/response interceptors
// (Sentry session, route headers, error handling) instead of bypassing them.
const retryRequestWithToken = (instance: AxiosInstance, config: AxiosRequestConfig, token: string) =>
  instance({
    ...config,
    headers: {
      ...config.headers,
      Authorization: `Bearer ${token}`,
    },
  });

//  in case of error — created per instance so retries use the right one
export const createOnErrorInterceptor = (instance: AxiosInstance) => async (error: AxiosError) => {
  if (error.response) {
    if (isApiError(error.response?.data)) {
      const { code } = error.response.data;
      if (error.response.status === 403 && code === ERROR_STATUS_CODES.EXTERNAL_UNAUTHORIZED) {
        window.location.replace(AppRoutes.externalPageNotFound);
        return;
      }
    }

    const originalConfig = error.config as RetryableConfig | undefined;
    const isRefreshTokenRequest = originalConfig?.url?.includes("/refresh-token");

    // Guard against infinite loops: a request that already got retried once and
    // still 401s falls through to normal error handling instead of re-queuing.
    // Also guard against the refresh-token call itself 401'ing: since it goes
    // through this same instrumented instance, without this check it would get
    // queued (never marked _isRetry) while isRefreshing is already true, which
    // deadlocks the outer `await refreshTokenApi(...)` and prevents its catch
    // block from ever running the logout/redirect flow.
    if (error.response.status === 401 && originalConfig && !originalConfig._isRetry && !isRefreshTokenRequest) {
      originalConfig._isRetry = true;

      // Queue this request so it is retried with the fresh token once refresh
      // completes. The first 401 kicks off the refresh below; every other
      // parallel 401 simply waits in the queue with its own original config.
      const retryPromise = new Promise((resolve, reject) => {
        refreshQueue.push((token) => {
          if (!token) {
            reject(error.response?.data);
            return;
          }
          resolve(retryRequestWithToken(instance, originalConfig, token));
        });
      });

      if (!isRefreshing) {
        isRefreshing = true;

        void (async () => {
          try {
            const refreshToken = Cookies.get(CENTER_REFRESH_TOKEN_KEY);
            const accessToken = Cookies.get(CENTER_TOKEN_KEY);
            if (!refreshToken || !accessToken) {
              throw new Error("Missing refresh credentials");
            }

            // A request that raced ahead of setAxiosAuth (no Authorization
            // header) just needs the current cookie token — no refresh needed.
            if (originalConfig.headers?.Authorization) {
              await refreshTokenApi({ refreshToken, accessToken });
            }

            const newToken = Cookies.get(CENTER_TOKEN_KEY) ?? null;
            const queue = refreshQueue;
            refreshQueue = [];
            queue.forEach((retry) => retry(newToken));
          } catch (refreshError) {
            redirectToLogin({ clearToken: true });
            const queue = refreshQueue;
            refreshQueue = [];
            queue.forEach((retry) => retry(null));
          } finally {
            isRefreshing = false;
          }
        })();
      }

      return retryPromise;
    }
  }
  handleApiErrorWithCode(error);
  if (error.response?.status && error.response.status >= 500 && error.response.status !== 503) {
    Sentry.captureException(getCustomSentryExceptionError(error));
  }
  Sentry.endSession();
  return Promise.reject(error.response?.data);
};

clientApi.interceptors.request.use(
  (config) => {
    const matchRoutes = getMatchRoutes();
    config.headers["X-RouteName"] = matchRoutes.at(-1)?.id;
    const screenRouteUrl = new URL(window.location.href);
    screenRouteUrl.search = "";
    config.headers["X-RouteURL"] = screenRouteUrl;

    Sentry.startSession();
    return config;
  },
  (error) => {
    if (error.response?.status && error.response.status >= 500 && error.response.status !== 503) {
      Sentry.captureException(getCustomSentryExceptionError(error));
    }
    Sentry.endSession();
    return Promise.reject(error);
  },
);

globalApi.interceptors.request.use(
  (config) => {
    Sentry.startSession();
    return config;
  },
  (error) => {
    if (error.response?.status && error.response.status >= 500 && error.response.status !== 503) {
      Sentry.captureException(getCustomSentryExceptionError(error));
    }
    Sentry.endSession();
    return Promise.reject(error);
  },
);

clientApi.interceptors.response.use(onSuccess, createOnErrorInterceptor(clientApi));
globalApi.interceptors.response.use(onSuccess, createOnErrorInterceptor(globalApi));

export const setAxiosAuth = () => {
  const token = Cookies.get(CENTER_TOKEN_KEY);
  clientApi.defaults.headers.common.Authorization = `Bearer ${token}`;
  globalApi.defaults.headers.common.Authorization = `Bearer ${token}`;
};

export const setExternalAxiosAuth = (externalToken: string) => {
  clientApi.defaults.headers.common.Authorization = `Bearer ${externalToken}`;
  globalApi.defaults.headers.common.Authorization = `Bearer ${externalToken}`;
};

export const removeAxiosAuth = () => {
  clientApi.defaults.headers.common.Authorization = "";
  globalApi.defaults.headers.common.Authorization = "";
};

export const lazyApi = <T = void>(fn: () => Promise<T>): [AbortController, () => Promise<T>] => {
  const controller = new AbortController();

  return [controller, fn];
};
